import useLocalStorage from "use-local-storage";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import Confetti from "react-confetti";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Box,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  COMPONENT_CUSTOM_MAXHEIGHT,
  CRPS_ORANGE_LIGHT_BRIGHT,
  distinctGrades,
  fetchGetClassroomsAllV,
  fetchGetFailed,
  fetchGetSorted,
  fetchGetSortProgress,
  fetchGetStudentsAll,
  fetchPostSort,
  Grade,
  GRADES,
  intensities,
  parseClassroomV,
  parseStudentVFail,
  parseStudentVSorted,
  PSConflictIcon,
  psLocalStorageSorted,
  schoolGrades,
  SetSnackType,
  SettingsGrades,
  SortedWithIntensities,
  STUDENT_INTENSITY_THRESHOLD_HIGH,
  STUDENT_INTENSITY_THRESHOLD_VERYHIGH,
  StudentsNoNulls,
  timeout,
  totalSeats,
  totalSeatsOfGrade,
  VClassroomsNoNulls,
  VFailedNoNulls,
  VSortedNoNulls,
  VStudentsNoNulls,
} from "../../utils";
import { PowerSortButtonGroup } from "../admin";

export const Sorteds = (props: {
  disconnect: () => void;
  conflictClicked: (stuNum: number) => void;
  role: string;
  powerSorting: boolean;
  setPowerSorting: Dispatch<SetStateAction<boolean>>;
  setSnack: SetSnackType;
  settingsGrades: SettingsGrades;
}) => {
  const rowsOrder = (rows: SortedWithIntensities[]): SortedWithIntensities[] =>
    rows.toSorted((a, b) => GRADES.indexOf(a.s_grade) - GRADES.indexOf(b.s_grade) || a.c_name.localeCompare(b.c_name));

  const [storedSelectedRoomId, setStoredSelectedRoomId] = useLocalStorage(psLocalStorageSorted, 0);

  const [rows, setRows] = useState(Array<SortedWithIntensities>());
  const [rowsFailed, setRowsFailed] = useState(Array<VFailedNoNulls>());
  const [rowsUnplaced, setRowsUnplaced] = useState(Array<VStudentsNoNulls>());
  const [rowsLoading, setRowsLoading] = useState(false);
  const [classrooms, setClassrooms] = useState(Array<VClassroomsNoNulls>());
  const [selectedClassroom, setSelectedClassroom] = useState<VClassroomsNoNulls | null>(null);
  const [placed, setPlaced] = useState(Array<VStudentsNoNulls>());

  const [gradesCanBeSorted, setGradesCanBeSorted] = useState(Array<Grade>());
  const [rowsSortingProgress, setRowsSortingProgress] = useState(0);
  const [recycleConfetti, setRecycleConfetti] = useState(false);
  const [runConfetti, setRunConfetti] = useState(false);

  const unselected = !selectedClassroom;

  const failedConflicts = (failed: VFailedNoNulls[]) => failed.filter(f => !f.with_grade);

  const failedUnplaced = (failed: VFailedNoNulls[]) =>
    failed.filter(f => !!f.with_grade).map(f => parseStudentVFail(f));

  const transformSortedWithIntensities = (sorted: VSortedNoNulls[]): SortedWithIntensities[] =>
    sorted.map(i => {
      const stu = parseStudentVSorted(i);
      const { achievement, behaviour, resource, social } = intensities(stu);
      return {
        ...i,
        stu_int_ach: achievement,
        stu_int_beh: behaviour,
        stu_int_res: resource,
        stu_int_soc: social,
      };
    });

  const refresh = async () => {
    setRowsLoading(true);
    try {
      const [resultSorted, resultFailed, resultClassrooms, resultStudents] = await Promise.all([
        fetchGetSorted(),
        fetchGetFailed(),
        fetchGetClassroomsAllV(),
        fetchGetStudentsAll(),
      ]);
      if (!resultSorted || Object.keys(resultSorted).includes("error")) {
        props.disconnect();
        props.setSnack((resultSorted as any).error?.toString(), "error");
        return;
      }
      if (!resultFailed || Object.keys(resultFailed).includes("error")) {
        props.disconnect();
        props.setSnack((resultFailed as any).error?.toString(), "error");
        return;
      }
      if (!resultClassrooms || Object.keys(resultClassrooms).includes("error")) {
        props.disconnect();
        props.setSnack((resultStudents as any).error?.toString(), "error");
        return;
      }
      if (!resultStudents || Object.keys(resultStudents).includes("error")) {
        props.disconnect();
        props.setSnack((resultStudents as any).error?.toString(), "error");
        return;
      }

      const sorted = resultSorted as VSortedNoNulls[];
      const failed = resultFailed as VFailedNoNulls[];
      const classrooms = resultClassrooms as VClassroomsNoNulls[];
      const students = resultStudents as StudentsNoNulls[];

      setRows(rowsOrder(transformSortedWithIntensities(sorted)));

      setRowsFailed(failedConflicts(failed));
      setRowsUnplaced(failedUnplaced(failed));

      setClassrooms(classrooms.filter(c => sorted.map(s => s.c_id).includes(c.id)));

      setGradesCanBeSorted(distinctGrades(students));

      return { sorted, failed };
    } catch (err: any) {
      console.error(err);
    } finally {
      setRowsLoading(false);
    }
  };

  const refRoomId = useRef(new Map<number, HTMLDivElement | null>());

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const storedSorted = rows.find(r => r.c_id === storedSelectedRoomId);
    if (!storedSorted) return;
    const storedRoom = parseClassroomV(storedSorted);
    if (!storedRoom) return;
    const el = refRoomId.current.get(storedRoom.id);
    if (!el) return;
    el.scrollIntoView({ behavior: "instant", block: "center" });
    setSelectedClassroom(storedRoom);
  }, [rows]);

  useEffect(() => {
    if (!selectedClassroom?.id) return;
    setStoredSelectedRoomId(selectedClassroom.id);
  }, [selectedClassroom]);

  const sort = async (gradesToSort: Grade[]) => {
    setSelectedClassroom(null);
    setRowsFailed([] as VFailedNoNulls[]);
    setRowsUnplaced([] as VStudentsNoNulls[]);
    props.setPowerSorting(true);
    setRowsSortingProgress(0);
    try {
      const result = (await fetchPostSort(gradesToSort)) as any;
      if (Object.keys(result).includes("error")) {
        props.disconnect();
        props.setSnack(result.error?.toString(), "error");
        return;
      }

      const { sortOperationId } = result;
      const evSrcSortProgress = fetchGetSortProgress(sortOperationId);

      const evSrcSortErr = () => {
        props.setSnack(`Sort "${sortOperationId}" failed`, "error");
        props.setPowerSorting(false);
        setRowsSortingProgress(0);
        evSrcSortProgress.close();
      };

      evSrcSortProgress.onmessage = async ev => {
        const progress = Number(ev.data);
        if (isNaN(progress) || progress < 0) {
          console.error("EventSource invalid progress:", ev);
          evSrcSortProgress.close();
          return;
        }

        setRowsSortingProgress(progress);

        if (progress === 0) {
          console.error("EventSource 0 prog:", ev);
          evSrcSortErr();
        } else if (progress >= 100) {
          timeout(500).then(() => props.setPowerSorting(false)); // allow 100% animation
          const { sorted, failed } = (await refresh()) ?? {
            sorted: [] as VSortedNoNulls[],
            failed: [] as VFailedNoNulls[],
          };
          const sortedOfGr = sorted.filter(i => gradesToSort.includes(i.s_grade));
          const stus = sortedOfGr.length;
          const sortedClasses = Array.from(new Set(sortedOfGr.map(i => i.c_id)));
          const rooms = sortedClasses.length;
          const failsOfGr = failed.filter(i => gradesToSort.includes(i.s_grade));
          if (failsOfGr.length > 0) {
            props.setSnack(
              `Sorted ${stus} student${stus === 1 ? "" : "s"} into ${rooms} classroom${
                rooms === 1 ? "" : "s"
              }, but with ${failsOfGr.length} conflict${failsOfGr.length === 1 ? "" : "s"}`,
              "warning",
            );
          } else {
            timeout(1).then(() => {
              setRecycleConfetti(true);
              setRunConfetti(true);
              timeout(750)
                .then(() => setRecycleConfetti(false))
                .then(() => timeout(6000).then(() => setRunConfetti(false)));
            });
            props.setSnack(
              `Sorted ${stus} student${stus === 1 ? "" : "s"} into ${rooms} classroom${rooms === 1 ? "" : "s"}`,
              "success",
            );
          }
          evSrcSortProgress.close();
        }
      };

      evSrcSortProgress.onerror = ev => {
        console.error("EventSource error:", ev);
        evSrcSortErr();
      };
    } catch (err: any) {
      console.error(err);
      props.setSnack(err, "error");
    }
  };

  useMemo(() => {
    setPlaced(rows.filter(i => i.c_id === selectedClassroom?.id).map(i => parseStudentVSorted(i)));
  }, [rows, selectedClassroom]);

  const TableCellPlaced = ({ children, centre, sx, ...rest }: any) => (
    <TableCell
      sx={{
        textAlign: centre ? "center" : undefined,
        margin: 0,
        padding: 0,
        ...sx,
      }}
      {...rest}
    >
      <Box display="inline-flex" justifyContent={centre ? "center" : "left"} alignItems={centre ? "center" : "left"}>
        {children}
      </Box>
    </TableCell>
  );

  const columns = [
    { field: "intensity", label: "Intensity", centre: true, sx: { width: 60, minWidth: 60 } },
    { field: "grade", label: "Grade", centre: true, sx: { width: 70, minWidth: 70 } },
    { field: "last", label: "Last", centre: false, sx: { flex: 1 } },
    { field: "first", label: "First", centre: false, sx: { flex: 1 } },
    { field: "number", label: "ID #", centre: false, sx: { width: 60, minWidth: 60 } },
    { field: "sa_desc", label: "Achievement", centre: true, sx: { width: 50, minWidth: 50 } },
    { field: "sb_desc", label: "Behaviour", centre: true, sx: { width: 100, minWidth: 100 } },
    { field: "ss_desc", label: "Social", centre: true, sx: { width: 106, minWidth: 106 } },
    { field: "ell", label: "ELL", centre: true, sx: { width: 30, minWidth: 30 } },
    { field: "sp_ed_1", label: "SpEd1", centre: true, sx: { width: 55, minWidth: 55 } },
    { field: "sp_ed_2", label: "SpEd2", centre: true, sx: { width: 55, minWidth: 55 } },
    { field: "sp_ed_3", label: "SpEd3", centre: true, sx: { width: 55, minWidth: 55 } },
    { field: "spcl_ed_1", label: "SpClEd1", centre: true, sx: { width: 55, minWidth: 55 } },
    { field: "spcl_ed_2", label: "SpClEd2", centre: true, sx: { width: 55, minWidth: 55 } },
    { field: "conflict", label: "Conflict", centre: true, sx: { width: 50, minWidth: 50 } },
  ];
  const columnsToPrint = columns.filter(c => !["intensity", "conflict"].includes(c.field));

  const placedIntensities = placed.reduce(
    (acc, el) => {
      const { achievement, behaviour, resource, social } = intensities(el);
      acc.a += achievement;
      acc.b += behaviour;
      acc.r += resource;
      acc.s += social;
      return acc;
    },
    { a: 0, b: 0, r: 0, s: 0 },
  );

  // const totalIntensities = rows.reduce(
  //   (acc, el) => {
  //     acc.a += el.stu_int_ach;
  //     acc.b += el.stu_int_beh;
  //     acc.r += el.stu_int_res;
  //     acc.s += el.stu_int_soc;
  //     return acc;
  //   },
  //   { a: 0, b: 0, r: 0, s: 0 },
  // );

  return (
    <>
      <Box
        className="no-print"
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          height: COMPONENT_CUSTOM_MAXHEIGHT(0),
          maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(0),
          overflowY: "none",
        }}
      >
        <Confetti
          confettiSource={{ x: 250, y: 100, w: 50, h: 50 }}
          initialVelocityY={15}
          gravity={0.7}
          recycle={recycleConfetti}
          run={runConfetti}
          tweenDuration={750}
        />
        {props.powerSorting ? (
          <LinearProgress variant="determinate" value={rowsSortingProgress} />
        ) : rowsLoading ? (
          <LinearProgress />
        ) : (
          <Box sx={{ minHeight: "4px" }}></Box>
        )}
        <Stack
          sx={{
            mt: 0,
            opacity: rowsLoading || props.powerSorting ? 0.3 : 1,
            pointerEvents: rowsLoading || props.powerSorting ? "none" : "auto",
          }}
        >
          <Stack direction={"row"} spacing={5} sx={{ ml: 1, mt: 1 }}>
            <Box sx={{ pl: 1 }}>
              <PowerSortButtonGroup
                {...{
                  disabled: rowsLoading || props.powerSorting,
                  grades: schoolGrades(props.settingsGrades).filter(g => gradesCanBeSorted.includes(g)),
                  role: props.role,
                  sort,
                }}
              />
            </Box>
            <Button
              disabled={rowsLoading || props.powerSorting}
              onClick={ev => window.print()}
              startIcon={<PictureAsPdfIcon />}
              variant="contained"
            >
              Print
            </Button>
          </Stack>
          <Stack direction={"row"} spacing={10} sx={{ width: "100%", justifyContent: "center", my: 1 }}>
            {/* todo: maybe add ` + rowsUnplaced.length` */}
            <Typography component="div">Conflicts: {rowsFailed.length}</Typography>
            <Typography component="div">
              Seating: {rows.length}
              {" / "}
              {classrooms.reduce((acc, el) => (acc += totalSeats(el)), 0)}
            </Typography>
            <Typography component="div">
              Total AI: {rows.reduce((acc, el) => (acc += el.stu_int_ach), 0).toLocaleString("en-US")}
            </Typography>
            <Typography component="div">
              Total BI: {rows.reduce((acc, el) => (acc += el.stu_int_beh), 0).toLocaleString("en-US")}
            </Typography>
            <Typography component="div">
              Total RI: {rows.reduce((acc, el) => (acc += el.stu_int_res), 0).toLocaleString("en-US")}
            </Typography>
            <Typography component="div">
              Total Soc ±: {rows.reduce((acc, el) => (acc += el.stu_int_soc), 0).toLocaleString("en-US")}
            </Typography>
          </Stack>
          <Stack direction={"row"} spacing={1} sx={{ display: "flex" }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 250,
                maxWidth: 500,
                maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(82),
                overflowY: "auto",
              }}
            >
              <Typography component="div" variant="h6" color="text.secondary" sx={{ mx: 2 }}>
                PowerSorted Classrooms
              </Typography>
              <List dense>
                {classrooms
                  .sort(
                    (a, b) =>
                      b.seats0am - a.seats0am ||
                      b.seats0pm - a.seats0pm ||
                      b.seats1 - a.seats1 ||
                      b.seats2 - a.seats2 ||
                      b.seats3 - a.seats3 ||
                      b.seats4 - a.seats4 ||
                      b.seats5 - a.seats5 ||
                      b.seats6 - a.seats6 ||
                      b.seats7 - a.seats7 ||
                      b.seats8 - a.seats8 ||
                      b.seats9 - a.seats9 ||
                      b.seats10 - a.seats10 ||
                      b.seats11 - a.seats11 ||
                      b.seats12 - a.seats12 ||
                      b.seats13 - a.seats13,
                  )
                  .map(room => (
                    <ListItemButton
                      key={room.id}
                      ref={el => {
                        refRoomId.current.set(room.id, el);
                      }}
                      selected={room.id === selectedClassroom?.id}
                      onClick={() => setSelectedClassroom(room)}
                      sx={{ my: 0, py: 0.5 }}
                    >
                      <ListItemText
                        primary={`${room.classroom_name}`}
                        secondary={`${distinctGrades(rows.filter(i => i.c_id === room.id).map(i => parseStudentVSorted(i))).join(", ")} - ${
                          (room.prefix ?? "N/A") === "N/A" ? room.first : room.prefix
                        } ${room.last}`}
                        sx={{ my: 0 }}
                      />
                      {rows.filter(i => i.c_id === room.id).some(i => rowsFailed.map(f => f.s_id).includes(i.s_id)) ? (
                        <Box display="flex" justifyContent="flex-end">
                          <PSConflictIcon size={"medium"} disabled />
                        </Box>
                      ) : (
                        <></>
                      )}
                    </ListItemButton>
                  ))}
              </List>
            </Box>
            <Stack
              sx={{
                flex: 3,
                minWidth: 400,
                maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(60),
                overflowY: "none",
              }}
            >
              <Box>
                <Typography component="div" variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  Classroom Details
                </Typography>
                <Stack direction={"row"} spacing={3} sx={{ width: "100%" }}>
                  <Typography component="div">
                    Conflicts:{" "}
                    {unselected
                      ? "—"
                      : rowsFailed.filter(
                          f =>
                            f.sc_owner_desc !== "SYSTEM" &&
                            f.tc_owner_desc !== "SYSTEM" &&
                            placed.find(s => s.id === f.s_id),
                        ).length}
                  </Typography>
                  <Typography component="div">
                    Seating:{" "}
                    {unselected ? (
                      "—"
                    ) : (
                      <>
                        {`${placed.length} / ${totalSeats(selectedClassroom)}`}
                        {distinctGrades(placed).length < 2 ? null : (
                          <List>
                            {distinctGrades(placed).map(g => (
                              <ListItem
                                key={`seats-${g}`}
                                sx={{ m: 0, p: 0 }}
                              >{`${g}: ${placed.filter(i => i.grade === g).length} / ${totalSeatsOfGrade(selectedClassroom, g)}`}</ListItem>
                            ))}
                          </List>
                        )}
                      </>
                    )}
                  </Typography>
                  <Typography component="div">
                    Ach. Int.: {unselected ? "—" : placedIntensities.a.toLocaleString("en-US")}
                  </Typography>
                  <Typography component="div">
                    Beh. Int.: {unselected ? "—" : placedIntensities.b.toLocaleString("en-US")}
                  </Typography>
                  <Typography component="div">
                    Res. Int.: {unselected ? "—" : placedIntensities.r.toLocaleString("en-US")}
                  </Typography>
                  <Typography component="div">
                    Soc. ±: {unselected ? "—" : placedIntensities.s.toLocaleString("en-US")}
                  </Typography>
                </Stack>
              </Box>
              <Typography component="div" variant="h6" color="text.secondary" sx={{ mt: 4, mb: 1 }}>
                Placed Students
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100vh",
                  maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(226),
                  overflowY: "auto",
                }}
              >
                <TableContainer component={Paper} sx={{ flex: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {columns.map((col, i) => (
                          <TableCellPlaced key={`${col.field}-h-${i}`} {...col}>
                            {col.label}
                          </TableCellPlaced>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unselected ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No Classroom Selected
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : placed.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No rows
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        placed
                          .toSorted(
                            (a, b) =>
                              GRADES.indexOf(a.grade) - GRADES.indexOf(b.grade) ||
                              a.last.localeCompare(b.last) ||
                              a.first.localeCompare(b.first),
                          )
                          .map(stu => {
                            const { achievement, resource, social } = intensities(stu);
                            const stuIntensity = achievement + resource + social;
                            return (
                              <TableRow key={stu.id}>
                                {columns.map((col, i) => (
                                  <TableCellPlaced key={`${col.field}-d-${i}`} {...col}>
                                    {col.field === "intensity" ? (
                                      stuIntensity > STUDENT_INTENSITY_THRESHOLD_VERYHIGH ? (
                                        <KeyboardDoubleArrowUpIcon />
                                      ) : stuIntensity > STUDENT_INTENSITY_THRESHOLD_HIGH ? (
                                        <KeyboardArrowUpIcon />
                                      ) : (
                                        <HorizontalRuleIcon />
                                      )
                                    ) : col.field === "conflict" ? (
                                      rowsFailed
                                        .filter(f => f.sc_owner_desc !== "SYSTEM")
                                        .find(f => f.s_id === stu.id) ? (
                                        <PSConflictIcon size={"medium"} onClick={() => props.conflictClicked(stu.id)} />
                                      ) : (
                                        <></>
                                      )
                                    ) : (
                                      stu[col.field as keyof VStudentsNoNulls]
                                    )}
                                  </TableCellPlaced>
                                ))}
                              </TableRow>
                            );
                          })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              {rowsUnplaced.length === 0 ? (
                <></>
              ) : (
                <Box>
                  <Typography component="div" variant="h6" color="text.secondary" sx={{ mt: 4, mb: 1 }}>
                    {rowsUnplaced.length} Unplaced Student{rowsUnplaced.length === 1 ? "" : "s"}
                  </Typography>
                  <TableContainer component={Paper} sx={{ minHeight: 100 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {columns.map((col, i) => (
                            <TableCellPlaced key={`${col.field}-h-${i}`} {...col}>
                              {col.label}
                            </TableCellPlaced>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rowsUnplaced.map(unplacedStu => {
                          const { achievement, resource, social } = intensities(unplacedStu);
                          const stuIntensity = achievement + resource + social;
                          return (
                            <TableRow key={unplacedStu.id}>
                              {columns.map((col, i) => (
                                <TableCellPlaced key={`${col.field}-d-${i}`} {...col}>
                                  {col.field === "intensity" ? (
                                    stuIntensity > STUDENT_INTENSITY_THRESHOLD_VERYHIGH ? (
                                      <KeyboardDoubleArrowUpIcon />
                                    ) : stuIntensity > STUDENT_INTENSITY_THRESHOLD_HIGH ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <HorizontalRuleIcon />
                                    )
                                  ) : col.field === "conflict" ? (
                                    <PSConflictIcon size={"small"} disabled />
                                  ) : (
                                    unplacedStu[col.field as keyof VStudentsNoNulls]
                                  )}
                                </TableCellPlaced>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <div className="sorted-print">
        {Array.from(new Set(rows.map(r => r.c_name))).map((roomName, kRoom) => (
          <div key={kRoom} className="sorted-print-pg">
            <Typography variant="h6" sx={{ color: CRPS_ORANGE_LIGHT_BRIGHT }}>
              {roomName}
            </Typography>
            <TableContainer component={Paper} sx={{ flex: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columnsToPrint.map((col, i) => (
                      <TableCellPlaced key={`${col.field}-h-${i}`} {...col}>
                        {col.label}
                      </TableCellPlaced>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows
                    .filter(r => r.c_name === roomName)
                    .map(iSorted => {
                      const stu = parseStudentVSorted(iSorted);
                      const { achievement, resource, social } = intensities(stu);
                      const stuIntensity = achievement + resource + social;
                      return (
                        <TableRow key={stu.id}>
                          {columnsToPrint.map((col, i) => (
                            <TableCellPlaced key={`${col.field}-d-${i}`} {...col}>
                              {col.field === "intensity" ? (
                                stuIntensity > STUDENT_INTENSITY_THRESHOLD_VERYHIGH ? (
                                  <KeyboardDoubleArrowUpIcon />
                                ) : stuIntensity > STUDENT_INTENSITY_THRESHOLD_HIGH ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <HorizontalRuleIcon />
                                )
                              ) : col.field === "conflict" ? (
                                rowsFailed.filter(f => f.sc_owner_desc !== "SYSTEM").find(f => f.s_id === stu.id) ? (
                                  <PSConflictIcon size={"medium"} onClick={() => props.conflictClicked(stu.id)} />
                                ) : (
                                  <></>
                                )
                              ) : (
                                stu[col.field as keyof VStudentsNoNulls]
                              )}
                            </TableCellPlaced>
                          ))}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ))}
      </div>
    </>
  );
};
